import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { ListService } from '../../../shared/services/list.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { catchError, of, switchMap } from 'rxjs';
import { ApiError } from '../../../shared/models/error';
import { List } from '../../../shared/types/list';
import { UserSessionService } from '../../../shared/services/user-session.service';
import { DestroyRxComponent } from '../../../components/destroy-rx/destroy-rx.component';
import { takeUntil } from 'rxjs/operators';
import { Icons } from '../../../shared/enums/icons';
import { listSaveEditLabel } from '../../../shared/utils/list-utils';

@Component({
    selector: 'las-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    standalone: false,
})
export class ListComponent extends DestroyRxComponent {
    icons = Icons;

    @ViewChild('newDialog', { static: true })
    newDialog!: TemplateRef<any>;
    @Input() canEdit = false;
    @Input() heading = '';
    @Input() anchor = '';
    @Input() type = '';

    lists!: List[];
    isLoading = true;
    isEditing = false;
    isSaving = false;
    error?: string = undefined;
    listName = '';
    click: Function;

    /**
     * Sets the initial edit/save click handler.
     * @param listService Service to manage the lists.
     * @param dialog Dialog to handle list updates.
     * @param sessionService Service providing the session$ observable.
     */
    constructor(
        private listService: ListService,
        private dialog: MatDialog,
        private sessionService: UserSessionService,
    ) {
        super();
        this.click = this.toggleEdit;
    }

    /**
     * Sets the loading and saving state.
     * @param state The current state.
     */
    set state(state: boolean) {
        this.isLoading = this.isSaving = state;
    }

    /**
     * Constructs the pipeline to reload the lists on session change.
     */
    ngOnInit() {
        this.sessionService.session$
            .pipe(
                // NOTE: We use switchMap to cover the case where a user quickly selects sessions one after another
                //  creating a race condition. This way, the previous http call is canceled.
                switchMap((_) => {
                    this.lists = [];
                    this.isLoading = true;
                    return this.getLists();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((listData) => this.resetState(listData));
    }

    /**
     * Saves the new lists' state and reloads the lists.
     */
    save() {
        this.prepForSave();
        this.listService
            .saveOrder(this.type, this.lists)
            .pipe(
                switchMap((_) => this.listService.getByType(this.type, true)),
                catchError((error) => this.handleError(error)),
            )
            .subscribe((listData) => {
                this.resetState(listData);
                this.toggleEdit();
            });
    }

    /**
     * Returns true if the lists have elements; false, otherwise.
     */
    hasLists = () => this.lists?.length > 0;

    /**
     * Toggles the edit/save state and sets the click handler accordingly.
     */
    toggleEdit() {
        this.isEditing = !this.isEditing;
        this.click = this.isEditing ? this.save : this.toggleEdit;
    }

    /**
     * Saves a newly created list item and reloads the lists.
     */
    createNewList() {
        // This is redundant to the if on the Save button on the dialog but, jic.
        if (this.listName === '') return;
        this.prepForSave();
        this.listService
            .createNewList(this.type, this.listName)
            .pipe(
                switchMap((_) => this.listService.getByType(this.type, true)),
                catchError((error) => this.handleError(error)),
            )
            .subscribe((listData) => {
                this.resetState(listData);
                this.dialog.closeAll();
            });
    }

    /**
     * Removes the list from the list; user must save to persist the change.
     */
    deactivateListItem(list: List) {
        list.active = false;
    }

    /**
     * Handles a drag/drop event on the list.
     * @param event A drag/drop event.
     */
    drop(event: CdkDragDrop<List[]>) {
        moveItemInArray(this.lists, event.previousIndex, event.currentIndex);
    }

    /**
     * Opens the dialog to add a new list.
     */
    openNewListDialog() {
        const dialog = this.dialog.open(this.newDialog);
        dialog.afterClosed().subscribe((_) => (this.listName = ''));
    }

    /**
     * Gets the lists for the type.
     * @param invalidate Flag to indicate whether the list should be retrieved from the cache, if possible.
     */
    getLists = (invalidate = false) =>
        this.listService
            .getByType(this.type, invalidate)
            .pipe(catchError((error) => this.handleError(error)));

    /**
     * Gets a typed list for use in ng-template.
     * @param list The list from the view.
     */
    asList = (list: any) => list as List;

    private prepForSave() {
        this.clearError();
        this.isSaving = true;
    }

    private resetState(lists: List[]) {
        this.lists = lists;
        this.state = false;
    }

    private handleError(error: ApiError) {
        // We need to check the error and clean up any failed attempts.
        this.lists ??= [];
        this.lists.forEach((list) => (list.active = true));
        this.state = false;
        this.error = error.message;
        return of(this.lists);
    }

    private clearError = () => (this.error = undefined);
    protected readonly listSaveEditLabel = listSaveEditLabel;
}
